"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { usePublicLocale } from "../../public-locale";
import {
  isMarketingFormName, MARKETING_ANALYTICS_STORAGE_KEY, MARKETING_LEAD_EVENT,
  savedMarketingConsent, type MarketingConsent,
} from "../../../lib/marketing-analytics";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const copy = {
  en: {
    title: "Choose your analytics cookies",
    desc: "With your permission, Google Analytics 4 helps measure site visits, visits from services such as ChatGPT, and confirmed enquiries. No advertising tracking. Rejecting does not affect the site.",
    accept: "Accept analytics", decline: "Reject analytics",
    manage: "Cookie settings", privacy: "Privacy policy",
  },
  fr: {
    title: "Choisissez vos cookies de mesure",
    desc: "Avec votre accord, Google Analytics 4 mesure les visites, les visites provenant de services comme ChatGPT et les demandes de contact confirmées. Pas de suivi publicitaire. Refuser ne change rien au site.",
    accept: "Accepter la mesure", decline: "Refuser la mesure",
    manage: "Gestion des cookies", privacy: "Confidentialité",
  },
  ar: {
    title: "إعدادات ملفات تعريف الارتباط للتحليلات",
    desc: "بموافقتك نستخدم Google Analytics 4 لقياس زيارات الموقع والزيارات القادمة من خدمات مثل ChatGPT وطلبات التواصل المؤكدة. لا نستخدم تتبعاً إعلانياً، والرفض لا يؤثر على الموقع.",
    accept: "الموافقة على التحليلات", decline: "رفض التحليلات",
    manage: "إعدادات ملفات الارتباط", privacy: "سياسة الخصوصية",
  },
} as const;

function clearGaCookies() {
  const names = document.cookie.split(";").map((part) => (part.trim().split("=")[0] ?? ""))
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  for (const name of names) {
    for (const domain of ["", "; domain=" + window.location.hostname, "; domain=.optrixislocal.com"]) {
      document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/" + domain + "; SameSite=Lax";
    }
  }
}

function loadGa4AfterConsent(id: string) {
  if (document.getElementById("optrixis-ga4-script")) return;
  window.dataLayer ||= [];
  // Google's gtag.js command processor expects an Arguments object, not an
  // ordinary array. Pushing an array silently prevents GA4 from sending hits.
  // Keep the official queue shape used by the standard Google tag snippet.
  window.gtag = function gtag() { window.dataLayer?.push(arguments); };
  window.gtag("consent", "default", {
    analytics_storage: "granted", ad_storage: "denied",
    ad_user_data: "denied", ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const script = document.createElement("script");
  script.id = "optrixis-ga4-script";
  script.async = true;
  script.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(id);
  document.head.appendChild(script);
}

export default function MarketingAnalyticsConsent({ measurementId }: { measurementId: string }) {
  const locale = usePublicLocale();
  const pathname = usePathname();
  const c = copy[locale];
  const [ready, setReady] = useState(false);
  const [consent, setConsent] = useState<MarketingConsent | null>(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const lastPath = useRef("");

  useEffect(() => {
    let previous: MarketingConsent | null = null;
    try { previous = savedMarketingConsent(localStorage.getItem(MARKETING_ANALYTICS_STORAGE_KEY)); }
    catch { /* Storage disabled: visitor must explicitly choose each time. */ }
    setConsent(previous);
    setReady(true);
  }, []);

  useEffect(() => {
    function onChange(event: StorageEvent) {
      if (event.key !== MARKETING_ANALYTICS_STORAGE_KEY) return;
      const next = savedMarketingConsent(event.newValue);
      // When another tab withdraws consent, unload any existing GA code.
      if (consent === "accepted" && next !== "accepted") { window.location.reload(); return; }
      setConsent(next);
    }
    window.addEventListener("storage", onChange);
    return () => window.removeEventListener("storage", onChange);
  }, [consent]);

  useEffect(() => {
    if (!ready || consent !== "accepted" || !pathname) return;
    loadGa4AfterConsent(measurementId);
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    // Preserve only the known ChatGPT referral marker when present. Never send
    // arbitrary URL parameters, which can contain email addresses or tokens.
    const fromChatGPT = new URLSearchParams(window.location.search)
      .get("utm_source")?.toLowerCase() === "chatgpt.com";
    const safeLocation = window.location.origin + pathname
      + (fromChatGPT ? "?utm_source=chatgpt.com" : "");
    window.gtag?.("event", "page_view", {
      page_title: document.title, page_path: pathname,
      page_location: safeLocation,
    });
  }, [consent, measurementId, pathname, ready]);

  useEffect(() => {
    if (consent !== "accepted") return;
    function onLead(event: Event) {
      const detail = (event as CustomEvent<{ formType?: unknown }>).detail;
      if (!isMarketingFormName(detail?.formType)) return;
      // Send only the form category: never visitor-entered fields or PII.
      window.gtag?.("event", "generate_lead", { form_type: detail.formType });
    }
    window.addEventListener(MARKETING_LEAD_EVENT, onLead);
    return () => window.removeEventListener(MARKETING_LEAD_EVENT, onLead);
  }, [consent]);

  function choose(next: MarketingConsent) {
    try { localStorage.setItem(MARKETING_ANALYTICS_STORAGE_KEY, next); }
    catch { /* No persistence is possible. */ }
    setPreferencesOpen(false);
    if (consent === "accepted" && next === "declined") {
      window.gtag?.("consent", "update", {
        analytics_storage: "denied", ad_storage: "denied",
        ad_user_data: "denied", ad_personalization: "denied",
      });
      clearGaCookies();
      window.location.reload();
      return;
    }
    setConsent(next);
  }

  if (!ready) return null;
  return (
    <>
      {consent !== null && !preferencesOpen ? (
        <button type="button" aria-label={c.manage} onClick={() => setPreferencesOpen(true)}
          className="optrixis-consent-manage"
          style={{ background: "var(--surface)", color: "var(--foreground)", borderColor: "var(--border)" }}>
          {c.manage}
        </button>
      ) : null}
      {consent === null || preferencesOpen ? (
        <section role="region" aria-labelledby="optrixis-marketing-consent-title" dir={locale === "ar" ? "rtl" : "ltr"}
          className="optrixis-consent-banner"
          style={{ background: "var(--surface)", color: "var(--foreground)", borderColor: "var(--border)" }}>
          <h2 id="optrixis-marketing-consent-title" className="optrixis-consent-heading" style={{ fontFamily: "'Sora', sans-serif" }}>
            {c.title}
          </h2>
          <p className="optrixis-consent-description" style={{ color: "var(--muted)" }}>{c.desc}</p>
          <a href="/privacy" className="optrixis-consent-privacy" style={{ color: "#4D00D3" }}>
            {c.privacy}
          </a>
          <div className="optrixis-consent-actions">
            <button type="button" onClick={() => choose("accepted")} className="optrixis-consent-accept">{c.accept}</button>
            <button type="button" onClick={() => choose("declined")} className="optrixis-consent-reject">{c.decline}</button>
          </div>
        </section>
      ) : null}
    </>
  );
}
